import {computeConvertedAmount, formatExchangeRateDisplay} from './currencyConversion'

export const QUOTATION_COMPARISON_VENDOR_COLUMN_ORDER = [
  'Supply Cap.',
  'Award Qty',
  'Allocated',
  'Unit Price',
  'Qty',
  'Discount %',
  'GST/VAT %',
  'Amount',
  'Lead Days',
  'Remark',
] as const

export const readComparisonQuantity = (line: {quantity?: unknown} | null | undefined) =>
  line?.quantity ?? '-'

export type QuotationComparisonMetadata = {
  comparableInCompanyCurrency: boolean
  comparisonAmount: number | null
  comparisonCurrency: string | null
  comparisonUnavailableReason: string | null
  originalAmount: number | null
  originalCurrency: string
  companyAmount: number | null
  companyCurrency: string | null
  exchangeRate: number | null
  exchangeRateDisplay: string | null
  snapshotEnabled: boolean
  historicalSameCurrencyComparable: boolean
}

const LEGACY_UNAVAILABLE_MESSAGE = 'Company currency conversion unavailable'

export const readFiniteAmount = (value: unknown): number | null => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

/** Presentation rule for individual logistics rows; stored/calculated values are untouched. */
export const isNonZeroLogisticsValue = (value: unknown): boolean => {
  const numeric = readFiniteAmount(value)
  return numeric != null && numeric !== 0
}

/** Uses the same line-pricing semantics as the comparison grid. */
export const calculateQuotationLineAmount = (
  quantity: unknown,
  unitPrice: unknown,
  discountPercent: unknown,
  gstPercent: unknown
) => {
  const qty = Math.max(0, readFiniteAmount(quantity) ?? 0)
  const price = readFiniteAmount(unitPrice) ?? 0
  const discount = readFiniteAmount(discountPercent) ?? 0
  const gst = readFiniteAmount(gstPercent) ?? 0
  const discounted = qty * price * (1 - discount / 100)
  return discounted + (discounted * gst) / 100
}

export const resolveQuotationDisplayCurrency = (quotation: any, fallback: string = 'USD') => {
  const snapshotCurrency = String(quotation?.quotationCurrencyCode || '').trim().toUpperCase()
  if (snapshotCurrency) return snapshotCurrency
  const quotationCurrencyCode = String(quotation?.quotationCurrency || '').trim().toUpperCase()
  const customCurrency = String(quotation?.customCurrency || '').trim()
  if (customCurrency && (quotationCurrencyCode === 'CUSTOM' || quotationCurrencyCode === 'OTHER')) {
    return customCurrency.toUpperCase()
  }
  if (String(quotation?.quotationCurrency || '').trim()) {
    return String(quotation.quotationCurrency).trim().toUpperCase()
  }
  if (String(quotation?.currency || '').trim()) {
    return String(quotation.currency).trim().toUpperCase()
  }
  if (customCurrency) return customCurrency.toUpperCase()
  return fallback.toUpperCase()
}

export const resolveQuotationCompanyCurrency = (quotation: any, fallback?: string | null) => {
  const companyCurrencyCode = String(quotation?.companyCurrencyCode || '').trim().toUpperCase()
  if (companyCurrencyCode) return companyCurrencyCode
  const normalizedFallback = String(fallback || '').trim().toUpperCase()
  return normalizedFallback || null
}

export const resolveQuotationOriginalTotal = (quotation: any) => {
  return readFiniteAmount(quotation?.originalTotal ?? quotation?.totalAmount)
}

export const resolveQuotationComparisonMetadata = (
  quotation: any,
  fallbackCompanyCurrency?: string | null
): QuotationComparisonMetadata => {
  const originalCurrency = resolveQuotationDisplayCurrency(quotation)
  const companyCurrency = resolveQuotationCompanyCurrency(quotation, fallbackCompanyCurrency)
  const originalAmount = resolveQuotationOriginalTotal(quotation)
  const snapshotCompanyAmount = readFiniteAmount(quotation?.companyTotal)
  const exchangeRate = readFiniteAmount(quotation?.exchangeRate)
  const exchangeRateDisplay =
    String(quotation?.exchangeRateDisplay || '').trim() ||
    (exchangeRate != null && companyCurrency
      ? formatExchangeRateDisplay(originalCurrency, exchangeRate, companyCurrency)
      : null)
  const snapshotEnabled = snapshotCompanyAmount != null && Boolean(companyCurrency)

  if (snapshotEnabled) {
    return {
      comparableInCompanyCurrency: true,
      comparisonAmount: snapshotCompanyAmount,
      comparisonCurrency: companyCurrency,
      comparisonUnavailableReason: null,
      originalAmount,
      originalCurrency,
      companyAmount: snapshotCompanyAmount,
      companyCurrency,
      exchangeRate,
      exchangeRateDisplay,
      snapshotEnabled: true,
      historicalSameCurrencyComparable: false,
    }
  }

  const sameCurrencyComparable =
    originalAmount != null &&
    Boolean(companyCurrency) &&
    originalCurrency === companyCurrency

  if (sameCurrencyComparable) {
    return {
      comparableInCompanyCurrency: true,
      comparisonAmount: originalAmount,
      comparisonCurrency: companyCurrency,
      comparisonUnavailableReason: null,
      originalAmount,
      originalCurrency,
      companyAmount: originalAmount,
      companyCurrency,
      exchangeRate: 1,
      exchangeRateDisplay: null,
      snapshotEnabled: false,
      historicalSameCurrencyComparable: true,
    }
  }

  return {
    comparableInCompanyCurrency: false,
    comparisonAmount: null,
    comparisonCurrency: companyCurrency,
    comparisonUnavailableReason: LEGACY_UNAVAILABLE_MESSAGE,
    originalAmount,
    originalCurrency,
    companyAmount: null,
    companyCurrency,
    exchangeRate,
    exchangeRateDisplay,
    snapshotEnabled: false,
    historicalSameCurrencyComparable: false,
  }
}

export const convertOriginalAmountToCompany = (
  amount: number | null | undefined,
  quotationOrMetadata: any
) => {
  const originalAmount = readFiniteAmount(amount)
  if (originalAmount == null) return null
  const metadata: QuotationComparisonMetadata =
    quotationOrMetadata?.comparableInCompanyCurrency != null
      ? quotationOrMetadata
      : resolveQuotationComparisonMetadata(quotationOrMetadata)
  if (!metadata.comparableInCompanyCurrency) return null
  if (metadata.historicalSameCurrencyComparable) return originalAmount
  return readFiniteAmount(
    computeConvertedAmount(
      originalAmount,
      metadata.exchangeRate,
      metadata.originalCurrency,
      metadata.companyCurrency || metadata.originalCurrency
    )
  )
}

export const resolveSelectedLineAmounts = (
  line: any,
  assignedQuantity: number,
  quotationOrMetadata: any
) => {
  const qty = Math.max(0, Number(assignedQuantity || 0))
  const baseQty =
    Math.max(
      0,
      Number(line?.assignedQuantity ?? line?.quantity ?? 0)
    ) || 0
  const metadata: QuotationComparisonMetadata =
    quotationOrMetadata?.comparableInCompanyCurrency != null
      ? quotationOrMetadata
      : resolveQuotationComparisonMetadata(quotationOrMetadata)

  const originalLineTotal = readFiniteAmount(line?.originalLineTotal)
  const companyLineTotal = readFiniteAmount(line?.companyLineTotal)
  const originalUnitPrice = readFiniteAmount(line?.originalUnitPrice ?? line?.unitPrice)
  const companyUnitPrice = readFiniteAmount(line?.companyUnitPrice)

  let selectedOriginalAmount: number | null = null
  if (originalLineTotal != null && baseQty > 0) {
    selectedOriginalAmount = (originalLineTotal * qty) / baseQty
  } else if (originalUnitPrice != null) {
    selectedOriginalAmount = originalUnitPrice * qty
  }

  let selectedCompanyAmount: number | null = null
  if (companyLineTotal != null && baseQty > 0) {
    selectedCompanyAmount = (companyLineTotal * qty) / baseQty
  } else if (companyUnitPrice != null) {
    selectedCompanyAmount = companyUnitPrice * qty
  } else {
    selectedCompanyAmount = convertOriginalAmountToCompany(selectedOriginalAmount, metadata)
  }

  return {
    selectedOriginalAmount: readFiniteAmount(selectedOriginalAmount),
    selectedCompanyAmount: readFiniteAmount(selectedCompanyAmount),
  }
}

export const LEGACY_COMPARISON_UNAVAILABLE_MESSAGE = LEGACY_UNAVAILABLE_MESSAGE
